import { ReactNode, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Container } from '../containers'
import { getColor } from 'utils'
import { devices } from '../../../utils/constants/media-queries'

interface ModalProps {
  isOpen: boolean
  requestClose: () => void
  children: ReactNode
  position: string | '';
  size?: 'normal' | 'wide' | 'full' | 'small';
  isClosing?: boolean;
}

interface IStyledModal{
  position: string,
  className: string,
  size: 'normal' | 'wide' | 'full' | 'small',
  isClosing?: boolean
}

const StyledModal = styled(Container)<IStyledModal>`
  position: fixed;
  top: 72px;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 210;
  transition: 0.3s ease-in-out all;
  &.hidden {
    visibility: hidden;
    opacity: 0;
  }

  &.closing .modal-overlay-container {
    opacity: 0;
    transition: opacity 0.15s ease-out;
  }

  &.closing .modal-content-container {
    opacity: 0;
    transform: scale(0.97);
    transition: opacity 0.25s ease-out, transform 0.25s ease-out;
  }

  .modal-overlay-container {
    display: none;
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
  }

  .modal-content-container {
    align-items: baseline;
    position: ${({position}:IStyledModal)=> position === 'top' ? 'absolute': 'relative'};
    top: ${({position}:IStyledModal)=> position === 'top' ? '70px' : ''};
    padding: ${({ size }: IStyledModal) => size === 'small' ? '16px' : '32px'};
    border-radius: 0;
    background-color: ${getColor('white')};
    opacity: 1;
    transform: translateY(0) scale(1);
    transition: max-width 280ms ease, width 280ms ease, transform 280ms ease, opacity 200ms ease;
    max-width: ${({ size }: IStyledModal) => {
      if (size === 'full') return '100vw';
      if (size === 'wide') return '75vw';
      if (size === 'small') return '360px';
      return '860px';
    }};
    width: 100%;
  }

  &.resizing .modal-content-container {
    opacity: 0.96;
    transform: translateY(-2px) scale(0.995);
  }

  @media only screen and ${devices.tablet}{
    top: 0;
    .modal-content-container {
      border-radius: 32px;
    }

    .modal-overlay-container {
      display: block;
      opacity: 1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.4);
    }
  }
  @media only screen and ${devices['2xl']}{
    .modal-content-container{
      top: ${({position}:IStyledModal)=> position === 'top' ? '100px' : ''};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .modal-content-container {
      transition: none;
    }
  }
`

export const Modal = (props: ModalProps) => {
  const { isOpen, children, requestClose, position, size = 'normal' } = props;
  const [isResizing, setIsResizing] = useState(false)
  const resizeTimerRef = useRef<number | null>(null)

  useEffect(() => {
    setIsResizing(true)
    if (resizeTimerRef.current) {
      window.clearTimeout(resizeTimerRef.current)
    }
    resizeTimerRef.current = window.setTimeout(() => {
      setIsResizing(false)
    }, 220)
    return () => {
      if (resizeTimerRef.current) {
        window.clearTimeout(resizeTimerRef.current)
      }
    }
  }, [size])

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [isOpen])

  const closingClass = props.isClosing ? ' closing' : '';
  return (
    <StyledModal position={position} size={size} className={`${isOpen ? 'modal' : 'hidden modal'}${isResizing ? ' resizing' : ''}${closingClass}`}>
      <Container className="modal-overlay" onClick={() => requestClose()} />
      <Container className="modal-content">{children}</Container>
    </StyledModal>
  )
}

export default Modal
